import React, { useEffect, useState, useContext, useRef } from 'react';
import { Context } from '../../../App';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Zoom from '@mui/material/Zoom';
import InputAdornment from '@mui/material/InputAdornment';
import InfoTwoToneIcon from '@mui/icons-material/InfoTwoTone';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import MuiSnackbar from '../../common/MuiSnackbar';
import CircularProgress from '@mui/material/CircularProgress';
import SeoHelmet from '../../common/SeoHelmet';
import CalculateButton from '../../common/calculateButton';
import SaveButton from '../../common/saveButton';
import Header from '../../common/Header';
import Footer from '../../common/Footer';
import Sidebar from '../../sidebar/Sidebar';
import DecisionModal from '../../modals/DecisionModal';
import CvVsLLUsNavyResult from './CvVsLLUsNavyResult';
import CvLLUsNavyGraphSvg from './CvLLUsNavyGraphSvg';
import PricingInfoModal from '../../modals/PricingInfoModal';
import Button from '@mui/material/Button';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { svgContainerToPngBase64 } from '../../../helpers/svgContainerToPngBase64';

import { useCalculationContext, CalculationContextProvider } from '../../context/calculationContext';
import useLoadSavedCalculation from '../useLoadSavedCalculation';

const ClayEffectiveFrictionAngle = () => {
  const { user } = useContext(Context);
  const {
    MAX_NAME_LENGTH,
    isLoadingCalculate,
    isLoadingLoadAll,
    isLoadingLoad,
    isLoadingSave,
    result,
    calculations,
    showOutput,
    setShowOutput,
    calculateButtonDisabled,
    setCalculateButtonDisabled,
    saveButtonDisabled,
    setSaveButtonDisabled,
    errorMessage,
    displaySidebar,
    setDisplaySidebar,
    decisionModalIsOpen,
    decision,
    setDecision,
    handleSidebarDisplay,
    setDecisionModalIsOpenToFalse,
    showMuiSnack,
    open,
    setOpen,
    alertMessage,
    severity,
    onChangeWarningForPositiveDecimals,
    calculate,
    loadOneCalculation,
    loadAllCalculations,
    update,
    checkCalculationNameforSaving,
    checkCalculationNameforSavingAs,
    deleteCalculations,
    calculationCountReached,
    saveCountReached,
  } = useCalculationContext();

  //SET STATES FOR INPUT VARIABLES
  const [name, setName] = useState('');
  const [liquidLimit, setLiquidLimit] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [showPdfWarningModal, setShowPdfWarningModal] = useState(false);
  const graphRef = useRef(null);

  // SET THE LOADED INPUTS
  const setInputs = (inputs) => {
    setName(inputs.name);
    setLiquidLimit(inputs.liquidLimit);
  };

  //INPUTS FOR CALCULATION
  const calculationInput = {
    name,
    liquidLimit,
  };

  // MANAGE SAVE & CALCULATE BUTTONS DISABLED/ENABLED STATES
  useEffect(() => {
    setCalculateButtonDisabled(false);
    setSaveButtonDisabled(false);

    if (
      (calculationInput.name || calculationInput.name === 0) &&
      (calculationInput.liquidLimit || calculationInput.liquidLimit === 0)
    ) {
      setCalculateButtonDisabled(false);
      setSaveButtonDisabled(false);
    } else {
      setCalculateButtonDisabled(true);
      setSaveButtonDisabled(true);
    }
  }, [setCalculateButtonDisabled, setSaveButtonDisabled, calculationInput.name, calculationInput.liquidLimit]);

  // Do not render output if any input parameter except name changes
  useEffect(() => {
    setShowOutput(false);
  }, [setShowOutput, calculationInput.liquidLimit]);

  //CLEAR INPUTS AND CLEAR OUTPUT
  const createNewCalculation = () => {
    setName('');
    setLiquidLimit('');
    setShowOutput(false);
  };

  //API URLS
  const URL = {
    CALCULATE: '/api/v1/calculations/cvLLUsNavy',
    LOADONE: `/api/v1/calculations/cvLLUsNavy/loadOne/`,
    LOADALL: '/api/v1/calculations/cvLLUsNavy/loadAll',
    UPDATE: `/api/v1/calculations/cvLLUsNavy/update/`,
    SAVE: '/api/v1/calculations/cvLLUsNavy/save',
    CHECKUNIQUENAME: `/api/v1/calculations/cvLLUsNavy/checkUniqueName/`,
    DELETE: '/api/v1/calculations/cvLLUsNavy/delete',
  };
  useLoadSavedCalculation({ URL, loadOneCalculation, calculate, setInputs, showMuiSnack });

  const handleChangeName = (e) => {
    if (e.target.value.length >= MAX_NAME_LENGTH) {
      showMuiSnack(`Calculation name can be max ${MAX_NAME_LENGTH} characters.`, 'warning');
    }
    setName(e.target.value);
  };

  const handleExportPdf = async () => {
    if (isExporting) return;
    if (user.role === 'basic' || user.role === 'standard') {
      setShowPdfWarningModal(true);
      return;
    }
    setIsExporting(true);
    try {
      await new Promise((resolve) => requestAnimationFrame(() => resolve()));
      const graph = await svgContainerToPngBase64(graphRef.current, {
        scale: 2,
        inlineImages: true,
      });

      if (!graph) {
        showMuiSnack('Graph capture failed. Please recalculate and try again.', 'warning');
        return;
      }

      const res = await fetch('/api/v1/pdf/cvLLUsNavy', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...calculationInput, graph }),
      });

      if (!res.ok) {
        throw new Error('PDF generation failed');
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'geocalc-cvllusnavy-report.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
    } finally {
      setIsExporting(false);
    }
  };

  //---------------------------------------------//
  // RETURN INPUT AND OUTPUT JSX
  return (
    <>
      <SeoHelmet
        title="GeoCalc | LL vs Cv (US Navy 1982) Calculator"
        description="Estimate the coefficient of consolidation (cv) from Liquid Limit (LL) using US Navy (1982) empirical relationships, with validity ranges and Cv-1/Cv-2/Cv-3 outputs."
        url="https://www.geocalc.app/calculations/cvLLUsNavy"
        image="https://www.geocalc.app/assets/og-theory-v1.jpg"
        schemaType="tool"
        breadcrumbs={[
          { name: 'Home', url: 'https://www.geocalc.app/' },
          { name: 'LL vs Cv (US Navy 1982) Calculator', url: 'https://www.geocalc.app/calculations/cvLLUsNavy' },
        ]}
      />

      <Header />
      <div className="container">
        <MuiSnackbar open={open} setOpen={setOpen} severity={severity} alertMessage={alertMessage} />
        {displaySidebar ? (
          <Sidebar
            URL={URL}
            loadAllCalculations={loadAllCalculations}
            calculations={calculations}
            loadOneCalculation={loadOneCalculation}
            calculate={calculate}
            setInputs={setInputs}
            deleteCalculations={deleteCalculations}
            createNewCalculation={createNewCalculation}
            isLoading={isLoadingLoadAll}
            handleSidebarDisplay={handleSidebarDisplay}
            displaySidebar={displaySidebar}
            setDisplaySidebar={setDisplaySidebar}
          />
        ) : (
          <MenuOpenIcon
            color="primary"
            fontSize="large"
            className="sidebar-open-icon"
            alt="side bar menu icon"
            label="Show my calculations"
            onClick={handleSidebarDisplay}
          />
        )}
        <DecisionModal
          URL={URL}
          data={calculationInput}
          decisionModalIsOpen={decisionModalIsOpen}
          setDecisionModalIsOpenToFalse={setDecisionModalIsOpenToFalse}
          decision={decision}
          setDecision={setDecision}
          checkCalculationNameforSavingAs={checkCalculationNameforSavingAs}
          name={name}
          setName={setName}
          errorMessage={errorMessage}
          update={update}
          saveButtonDisabled={saveButtonDisabled}
        />
        <PricingInfoModal open={showPdfWarningModal} setOpen={setShowPdfWarningModal} role={user.role} />
        <div className="right-calculation">
          <div className="cvLLUsNavyContainer">
            <h1>Liquid Limit (LL) vs Cv Relationship | US Navy 1982</h1>
            <div className="cvLLUsNavyInputContainer">
              <TextField
                style={{ minWidth: 80, maxWidth: 120 }}
                size="small"
                label="Name"
                variant="outlined"
                inputProps={{
                  maxLength: `${MAX_NAME_LENGTH}`,
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip
                        title={`Your calculation name (Max. ${MAX_NAME_LENGTH} characters)`}
                        arrow
                        placement="bottom"
                        TransitionComponent={Zoom}
                      >
                        <InfoTwoToneIcon fontSize={'string'} />
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                value={name}
                onChange={handleChangeName}
                disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
              />
              <TextField
                style={{ minWidth: 80, maxWidth: 120 }}
                size="small"
                label="LL"
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip
                        title={
                          <>
                            <div>Liquid Limit (LL) of clay (%).</div>
                            <br />
                            <div>For Cv-1 &rarr; valid LL(%) values range between 33-120. </div>
                            <br />
                            <div>For Cv-2 &rarr; valid LL(%) values range between 27-160. </div>
                            <br />
                            <div>For Cv-3 &rarr; valid LL(%) values range between 27-120. </div>
                            <br />
                            <div>If you use LL(%) values out of those ranges, no result will be shown on output!</div>
                          </>
                        }
                        arrow
                        placement="bottom"
                        TransitionComponent={Zoom}
                      >
                        <InfoTwoToneIcon fontSize={'string'} />
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                value={liquidLimit}
                onChange={(e) => onChangeWarningForPositiveDecimals(e, 'Plasticity Index', setLiquidLimit)}
                disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
              />
            </div>

            <div className="buttonContainer cvLLUsNavySaveCalculateButtonContainer">
              <CalculateButton
                calculationCountReached={calculationCountReached}
                calculate={calculate}
                calculationInput={calculationInput}
                URL={URL}
                calculateButtonDisabled={calculateButtonDisabled}
                isLoadingCalculate={isLoadingCalculate}
                isLoadingLoad={isLoadingLoad}
                isLoadingSave={isLoadingSave}
              />
              <Button
                variant="contained"
                startIcon={!isExporting ? <PictureAsPdfIcon /> : null}
                onClick={handleExportPdf}
                disabled={!showOutput || isExporting}
                style={{ width: '200px' }}
              >
                {isExporting ? (
                  <>
                    <CircularProgress size={18} style={{ marginRight: 8 }} />
                    Creating PDF
                  </>
                ) : (
                  'Create PDF'
                )}
              </Button>
              <SaveButton
                saveCountReached={saveCountReached}
                checkCalculationNameforSaving={checkCalculationNameforSaving}
                calculationInput={calculationInput}
                URL={URL}
                saveButtonDisabled={saveButtonDisabled}
                isLoadingCalculate={isLoadingCalculate}
                isLoadingLoad={isLoadingLoad}
                isLoadingSave={isLoadingSave}
              />
            </div>
            {isLoadingLoad && <CircularProgress size={30} />}
            {showOutput && <CvVsLLUsNavyResult result={result} />}
            {showOutput && result?.outputs && (
              <div
                ref={graphRef}
                style={{
                  position: 'absolute',
                  left: -99999,
                  top: -99999,
                  width: 600,
                  pointerEvents: 'none',
                  overflow: 'hidden',
                }}
              >
                <CvLLUsNavyGraphSvg
                  ll={result.inputs?.liquidLimit}
                  showOverlay
                  cvValues={[
                    {
                      key: 'cv1',
                      show: result.outputs.isCv1LimitOK,
                      cvM2PerYear: result.outputs.cv1_m2perYear,
                      cvCm2PerSecond: result.outputs.cv1_cm2PerSecond,
                    },
                    {
                      key: 'cv2',
                      show: result.outputs.isCv2LimitOK,
                      cvM2PerYear: result.outputs.cv2_m2perYear,
                      cvCm2PerSecond: result.outputs.cv2_cm2PerSecond,
                    },
                    {
                      key: 'cv3',
                      show: result.outputs.isCv3LimitOK,
                      cvM2PerYear: result.outputs.cv3_m2perYear,
                      cvCm2PerSecond: result.outputs.cv3_cm2PerSecond,
                    },
                  ]}
                />
              </div>
            )}
            {isLoadingCalculate && (
              <div className="centerLoading">
                <CircularProgress />
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

const ClayEffectiveFrictionAngleWithProvider = () => {
  return (
    <CalculationContextProvider>
      <ClayEffectiveFrictionAngle />
    </CalculationContextProvider>
  );
};
export default ClayEffectiveFrictionAngleWithProvider;
